import { useContext } from 'react'

import { faToggleOn } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import PageContext from '@contexts/pageContext'

import {
  Button, DashboardModule, Form, Text,
} from '@domain-group/suit-theme'

import EntitySelectBox from '@components/EntitySelectBox'

import useAddress from '@hooks/useAddress'
import { useAgencyForm } from '@hooks/useAgency'

import InfoToolTip from '@components/InfoToolTip'

import type { AddressModel, AgencyModel } from '@models/types'

interface PhysicalAddressProps {
  addresses: AddressModel[],
  agency: AgencyModel,
  agencyFormPayload: ReturnType<typeof useAgencyForm>,
}

const PhysicalAddress = (props: PhysicalAddressProps) => {
  const {
    addresses,
    agency,
    agencyFormPayload: {
      entityState: {
        address_line_1,
        address_line_2,
        address_region,
        address_postcode,
        address_state,
        geo_country_id,
      },
      errors,
      handlers,
    },
  } = props

  const pageContext = useContext(PageContext)
  const {
    callbacks: { resetDefaultAddress },
  } = pageContext

  const hasDefaultAddress = addresses.some(({ is_default }) => !!is_default)

  const { callbacks: { resetDefaultAddress: resetFn } } = useAddress()

  const resetDefaultPayload = {
    callbacks: {
      action: resetFn,
    },
    entityParams: { agency_id: agency.id },
    toastText: 'Default Shipping Address Updated',
  }

  return (
    <DashboardModule
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={(
        <InfoToolTip>
          Your office&apos;s physical address will be used to inform providers on delivery
          location as well as appearing in some templates.
          <br />
          <br />
          If you change office,
          you may need to contact providers directly as they could potentially
          cache this information on their systems.
        </InfoToolTip>
      )}
      title="Office Physical Address"
    >
      <Form padding="large">
        <Form.Row>
          <Form.Field errorMessage={errors.address_line_1} label="* Address Line 1">
            <input
              name="address_line_1"
              type="text"
              value={address_line_1}
              {...handlers}
            />
          </Form.Field>

          {/*
           // @ts-expect-error TS2322 */}
          <Form.Field label="Address Line 2" marginTop={['large', 0]}>
            <input
              name="address_line_2"
              type="text"
              value={address_line_2}
              {...handlers}
            />
          </Form.Field>
        </Form.Row>

        <Form.Row marginTop="large">
          <Form.Field label="* Suburb" errorMessage={errors.address_region}>
            <input
              name="address_region"
              value={address_region}
              type="text"
              {...handlers}
            />
          </Form.Field>

          {/*
           // @ts-expect-error TS2339 */}
          <Form.Field label="* Post Code" errorMessage={errors.address_postcode} marginTop={['large', 0]}>
            <input
              name="address_postcode"
              type="text"
              value={address_postcode}
              {...handlers}
            />
          </Form.Field>
        </Form.Row>

        <Form.Row marginTop="large">
          <Form.Field label="* State" errorMessage={errors.address_state}>
            <input
              name="address_state"
              type="text"
              value={address_state}
              {...handlers}
            />
          </Form.Field>

          {/*
           // @ts-expect-error TS2339 */}
          <Form.Field errorMessage={errors.geo_country_id} label="Country" marginTop={['large', 0]}>
            <EntitySelectBox
              data-value-type="number"
              entityKey="geoCountries"
              // @ts-expect-error TS2322
              name="geo_country_id"
              sortDirection="asc"
              sortKey="title"
              value={geo_country_id}
              {...handlers}
            />
          </Form.Field>
        </Form.Row>
      </Form>

      <DashboardModule.Footer>
        <Text fontSize="xsmall" color="bodyFontLightColor" marginTop="small">
          {hasDefaultAddress && 'Set this address as the agency\'s default shipping address'}
          {!hasDefaultAddress && 'This address is currently set as the default shipping address'}
        </Text>

        {hasDefaultAddress && (
          <DashboardModule.FooterRight>
            <Button
              buttonStyle="secondaryEdit"
              icon={<FontAwesomeIcon icon={faToggleOn} />}
              //@ts-expect-error
              marginTop="xsmall"
              onClick={() => resetDefaultAddress(resetDefaultPayload)}
              size="medium"
              width={['100%', 'fit-content']}
            >
              Set as Default
            </Button>
          </DashboardModule.FooterRight>
        )}
      </DashboardModule.Footer>
    </DashboardModule>
  )
}

export default PhysicalAddress
